// assets/chart-preview.js
// Live natal-chart preview. Reads the birth-data inputs from the product form, posts them to the
// chart-preview Worker (which holds the engine token), and renders the returned SVG in a dialog.

import { readBirthData, isComplete, firstMissing, onBirthDataChange } from './birth-data.js';

const MISSING_HINT = {
  date: 'Add your birth date above to preview.',
  time: 'Add your birth time above to preview.',
  place: 'Pick your birthplace above so we have its coordinates.',
};

class ChartPreview extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    /** @type {string} */
    this.endpoint = this.dataset.endpoint || '';
    this.template = this.dataset.template || 'wheel';

    /** @type {HTMLButtonElement | null} */
    this.openBtn = this.querySelector('[data-preview-open]');
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-preview-dialog]'));
    /** @type {HTMLElement | null} */
    this.svgBox = this.querySelector('[data-preview-svg]');
    /** @type {HTMLElement | null} */
    this.loading = this.querySelector('[data-preview-loading]');
    /** @type {HTMLElement | null} */
    this.hint = this.querySelector('[data-preview-hint]');

    if (!this.endpoint && this.openBtn) this.openBtn.disabled = true;

    this.openBtn?.addEventListener('click', () => this.open());
    this.querySelector('[data-preview-close]')?.addEventListener('click', () => this.dialog?.close());
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.dialog?.close();
    });

    /** @type {boolean} Whether the customer has opened the preview at least once. */
    this.opened = false;
    // Nudge toward the preview once all three birth fields are filled.
    onBirthDataChange(() => this.updateReady());
    this.updateReady();
  }

  /** Arm the button (subtle pulse + nudge) once ready, until it's been opened. */
  updateReady() {
    const armed = isComplete() && !this.opened;
    this.openBtn?.classList.toggle('is-armed', armed);
    if (!this.hint) return;
    if (armed) {
      this.hint.textContent = '✦ Your chart is ready to preview';
      this.hint.hidden = false;
    } else if (this.hint.textContent.startsWith('✦')) {
      // Only clear our own nudge; leave validation messages in place.
      this.hint.hidden = true;
    }
  }

  /** @param {string} msg */
  showHint(msg) {
    if (!this.hint) return;
    this.hint.textContent = msg;
    this.hint.hidden = false;
  }

  async open() {
    const endpoint = this.endpoint;
    if (!endpoint) return this.showHint('Preview isn’t configured yet.');

    const missing = firstMissing();
    if (missing) return this.showHint(MISSING_HINT[missing]);
    if (this.hint) this.hint.hidden = true;
    this.opened = true;
    this.openBtn?.classList.remove('is-armed');

    const { date, time, lat, lon } = readBirthData();

    if (this.svgBox) this.svgBox.innerHTML = '';
    if (this.loading) this.loading.hidden = false;
    this.dialog?.showModal();

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, time, lat: Number(lat), lon: Number(lon), template: this.template }),
      });
      const data = await res.json().catch(() => ({}));
      if (this.loading) this.loading.hidden = true;

      if (!res.ok || !data.svg) {
        if (this.svgBox) {
          this.svgBox.innerHTML =
            '<p class="stz-preview__error">' +
            (data.message || 'We couldn’t render that chart. Double-check your details and try again.') +
            '</p>';
        }
        return;
      }
      if (this.svgBox) this.svgBox.innerHTML = data.svg; // SVG string from our own engine via the Worker
    } catch (err) {
      if (this.loading) this.loading.hidden = true;
      if (this.svgBox) {
        this.svgBox.innerHTML = '<p class="stz-preview__error">Preview is unavailable right now. Please try again.</p>';
      }
    }
  }
}

if (!customElements.get('chart-preview')) {
  customElements.define('chart-preview', ChartPreview);
}
